import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  Mail, RefreshCw, Send, Loader2, Inbox, ArrowLeft, PenSquare,
  Building2, Link2, Paperclip, X, ShieldCheck, Unplug,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

function ConnectCard({ onConnected }) {
  const [address, setAddress] = useState("team@pollendots.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    if (!password.trim()) return toast.error("Paste the 16-character App Password first");
    setBusy(true);
    try {
      await api.post("/admin/mailbox/settings", { gmail_address: address, app_password: password });
      toast.success("Gmail connected");
      onConnected();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect to Gmail");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto mt-8 bg-white rounded-2xl border border-border p-6 space-y-4" data-testid="mailbox-connect-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-forest text-cream flex items-center justify-center"><Mail size={18} /></div>
        <div>
          <h2 className="text-lg font-semibold">Connect the team mailbox</h2>
          <p className="text-sm text-muted-foreground">Send &amp; receive vendor emails right here.</p>
        </div>
      </div>
      <ol className="text-sm text-muted-foreground list-decimal ml-5 space-y-1">
        <li>Turn on <b>2-Step Verification</b> for the Google account.</li>
        <li>Create an App Password at <a className="underline text-forest" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">myaccount.google.com/apppasswords</a>.</li>
        <li>Paste the 16-character password below — it's encrypted and never shown again.</li>
      </ol>
      <div className="space-y-2">
        <input data-testid="mailbox-address-input" className="w-full border rounded-lg px-3 py-2 text-sm" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="team@pollendots.com" />
        <input data-testid="mailbox-password-input" type="password" className="w-full border rounded-lg px-3 py-2 text-sm" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="16-character App Password" />
      </div>
      <button data-testid="mailbox-connect-btn" onClick={connect} disabled={busy}
        className="w-full flex items-center justify-center gap-2 bg-forest text-cream rounded-full py-2.5 text-sm font-medium disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Connect Gmail
      </button>
    </div>
  );
}

function ComposeModal({ onClose, onSent, presetTo }) {
  const [to, setTo] = useState(presetTo || "");
  const [subject, setSubject] = useState("");
  const [qsid, setQsid] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/admin/mailbox/send", { to, subject, text, query_short_id: qsid });
      toast.success("Email sent");
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="mailbox-compose-modal">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">New email</h3>
          <button data-testid="compose-close-btn" onClick={onClose} className="text-muted-foreground"><X size={18} /></button>
        </div>
        <input data-testid="compose-to-input" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="To — vendor@example.com" value={to} onChange={(e) => setTo(e.target.value)} />
        <input data-testid="compose-subject-input" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <input data-testid="compose-qry-input" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Query ID to stamp (optional) — QRY-20260601-001" value={qsid} onChange={(e) => setQsid(e.target.value)} />
        <textarea data-testid="compose-body-input" rows={7} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Message…" value={text} onChange={(e) => setText(e.target.value)} />
        <button data-testid="compose-send-btn" onClick={send} disabled={busy}
          className="w-full flex items-center justify-center gap-2 bg-forest text-cream rounded-full py-2.5 text-sm font-medium disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send
        </button>
      </div>
    </div>
  );
}

function MessageDetail({ mid, onBack, onSentReply }) {
  const [msg, setMsg] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/mailbox/messages/${mid}`).then((r) => {
      setMsg(r.data);
      if (r.data.folder === "inbox" && r.data.unread) api.post(`/admin/mailbox/messages/${mid}/read`).catch(() => {});
    }).catch(() => toast.error("Could not load message"));
  }, [mid]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/mailbox/messages/${mid}/reply`, { text: reply });
      toast.success("Reply sent" + (msg?.query_short_id ? ` · logged to ${msg.query_short_id}` : ""));
      setReply("");
      onSentReply();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed");
    } finally {
      setBusy(false);
    }
  };

  if (!msg) return <div className="p-8 text-center text-muted-foreground"><Loader2 className="animate-spin inline" size={18} /></div>;

  return (
    <div className="bg-white rounded-2xl border border-border p-5 space-y-4" data-testid="mailbox-message-detail">
      <button data-testid="message-back-btn" onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft size={14} /> Back to {msg.folder}</button>
      <div>
        <h2 className="text-lg font-semibold leading-snug">{msg.subject || "(no subject)"}</h2>
        <div className="text-sm text-muted-foreground mt-1">
          {msg.folder === "sent" ? <>To: {(msg.to || []).join(", ")}</> : <>{msg.from_name ? `${msg.from_name} · ` : ""}{msg.from_email}</>} · {fmtDate(msg.date)}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {msg.vendor_name && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-beige text-ink text-xs" data-testid="message-vendor-badge"><Building2 size={11} /> {msg.vendor_name}</span>}
          {msg.query_short_id && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-forest/10 text-forest text-xs" data-testid="message-qry-badge"><Link2 size={11} /> {msg.query_short_id}</span>}
          {msg.auto_logged_response_id && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs" data-testid="message-logged-badge"><ShieldCheck size={11} /> Logged as vendor response</span>}
        </div>
      </div>
      <div className="whitespace-pre-wrap text-sm leading-relaxed border-t pt-4" data-testid="message-body">{msg.text || "(empty message)"}</div>
      {(msg.attachments || []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {msg.attachments.map((a, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-beige text-xs"><Paperclip size={11} /> {a.filename} <span className="opacity-60">({Math.round((a.size || 0) / 1024)} KB)</span></span>
          ))}
        </div>
      )}
      {msg.folder === "inbox" && (
        <div className="border-t pt-4 space-y-2">
          <textarea data-testid="reply-body-input" rows={4} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder={`Reply to ${msg.from_email}…`} value={reply} onChange={(e) => setReply(e.target.value)} />
          <button data-testid="reply-send-btn" onClick={sendReply} disabled={busy || !reply.trim()}
            className="flex items-center gap-2 bg-forest text-cream rounded-full px-5 py-2 text-sm font-medium disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send reply{msg.query_short_id ? ` · logs to ${msg.query_short_id}` : ""}
          </button>
        </div>
      )}
    </div>
  );
}

export default function MailboxPage() {
  const [settings, setSettings] = useState(null);
  const [folder, setFolder] = useState("inbox");
  const [vendorOnly, setVendorOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [compose, setCompose] = useState(false);

  const loadSettings = useCallback(() => api.get("/admin/mailbox/settings").then((r) => setSettings(r.data)), []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/mailbox/messages", { params: { folder, vendor_only: vendorOnly, q: search } });
      setMessages(r.data.items || []);
      setUnread(r.data.unread || 0);
    } finally {
      setLoading(false);
    }
  }, [folder, vendorOnly, search]);

  const sync = useCallback(async (silent = false) => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/mailbox/sync");
      if (!silent) toast.success(`Synced — ${r.data.new} new, ${r.data.auto_logged} auto-logged`);
      await Promise.all([loadMessages(), loadSettings()]);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  }, [loadMessages, loadSettings]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { if (settings?.configured) loadMessages(); }, [settings?.configured, loadMessages]);
  useEffect(() => { if (settings?.configured && !settings?.last_sync_at) sync(true); }, [settings?.configured]); // eslint-disable-line react-hooks/exhaustive-deps

  const disconnect = async () => {
    if (!window.confirm("Disconnect the Gmail mailbox? Synced messages stay, but send/receive stops.")) return;
    await api.delete("/admin/mailbox/settings");
    setSettings({ configured: false });
    setSelected(null);
  };

  if (!settings) return <Layout><div className="p-10 text-center"><Loader2 className="animate-spin inline" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4" data-testid="mailbox-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2"><Mail size={22} className="text-forest" /> Mailbox</h1>
            <p className="text-sm text-muted-foreground">Vendor emails through {settings.gmail_address || "team@pollendots.com"} — replies auto-log to their query.</p>
          </div>
          {settings.configured && (
            <div className="flex items-center gap-2">
              <button data-testid="mailbox-sync-btn" onClick={() => sync()} disabled={syncing}
                className="flex items-center gap-1.5 border border-border rounded-full px-4 py-1.5 text-sm disabled:opacity-50">
                <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> Sync
              </button>
              <button data-testid="mailbox-compose-btn" onClick={() => setCompose(true)}
                className="flex items-center gap-1.5 bg-forest text-cream rounded-full px-4 py-1.5 text-sm">
                <PenSquare size={14} /> Compose
              </button>
            </div>
          )}
        </div>

        {!settings.configured ? (
          <ConnectCard onConnected={loadSettings} />
        ) : selected ? (
          <MessageDetail mid={selected} onBack={() => { setSelected(null); loadMessages(); }} onSentReply={() => loadMessages()} />
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex rounded-full border border-border overflow-hidden text-sm">
                <button data-testid="folder-inbox-btn" onClick={() => setFolder("inbox")}
                  className={`px-4 py-1.5 flex items-center gap-1.5 ${folder === "inbox" ? "bg-forest text-cream" : "bg-white"}`}>
                  <Inbox size={13} /> Inbox{unread > 0 && <span className="ml-0.5 px-1.5 rounded-full bg-pollen text-ink text-[10px] font-bold">{unread}</span>}
                </button>
                <button data-testid="folder-sent-btn" onClick={() => setFolder("sent")}
                  className={`px-4 py-1.5 flex items-center gap-1.5 ${folder === "sent" ? "bg-forest text-cream" : "bg-white"}`}>
                  <Send size={13} /> Sent
                </button>
              </div>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer select-none" data-testid="vendor-only-toggle">
                <input type="checkbox" checked={vendorOnly} onChange={(e) => setVendorOnly(e.target.checked)} className="accent-forest" />
                Vendors only
              </label>
              <input data-testid="mailbox-search-input" className="flex-1 min-w-[160px] border rounded-full px-4 py-1.5 text-sm"
                placeholder="Search subject, sender, QRY…" value={search}
                onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadMessages()} />
            </div>

            <div className="bg-white rounded-2xl border border-border divide-y" data-testid="mailbox-message-list">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground"><Loader2 className="animate-spin inline" size={18} /></div>
              ) : messages.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground" data-testid="mailbox-empty-state">
                  {folder === "inbox" ? "No messages yet — hit Sync to pull the latest from Gmail." : "Nothing sent from the app yet."}
                </div>
              ) : messages.map((m) => (
                <button key={m.id} data-testid={`message-row-${m.id}`} onClick={() => setSelected(m.id)}
                  className="w-full text-left px-4 py-3 hover:bg-cream/60 transition-colors flex flex-col gap-0.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${m.unread ? "font-semibold" : ""}`}>
                      {folder === "sent" ? `To: ${(m.to || []).join(", ")}` : (m.from_name || m.from_email)}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">{fmtDate(m.date)}</span>
                  </div>
                  <div className={`text-sm truncate ${m.unread ? "font-medium" : "text-muted-foreground"}`}>{m.subject || "(no subject)"}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {m.vendor_name && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-beige text-ink text-[10px]"><Building2 size={9} /> {m.vendor_name}</span>}
                    {m.query_short_id && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-forest/10 text-forest text-[10px]"><Link2 size={9} /> {m.query_short_id}</span>}
                    {m.auto_logged_response_id && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px]">logged ✓</span>}
                    {(m.attachments || []).length > 0 && <Paperclip size={11} className="text-muted-foreground" />}
                  </div>
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span data-testid="mailbox-last-sync">{settings.last_sync_at ? `Last synced ${fmtDate(settings.last_sync_at)} · auto-sync every 20 min` : "Not synced yet"}</span>
              <button data-testid="mailbox-disconnect-btn" onClick={disconnect} className="flex items-center gap-1 hover:text-red-600"><Unplug size={12} /> Disconnect</button>
            </div>
          </>
        )}

        {compose && <ComposeModal onClose={() => setCompose(false)} onSent={() => { setCompose(false); setFolder("sent"); loadMessages(); }} />}
      </div>
    </Layout>
  );
}
